"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  Notice,
  Panel,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { splitItems } from "@/lib/tool-kit/essay-templates/text";
import {
  buildSeminarQuestions,
  buildSeminarSheet,
  EMPTY_SEMINAR,
  type QuestionIssue,
  questionIssues,
  SEMINAR_CATEGORIES,
  SEMINAR_FRAMES,
  type SeminarCategory,
  type SeminarInput,
  selectQuestions,
} from "@/lib/tool-kit/lit-essays/seminar";

/** English strings. A translated content file overrides these through `ui`. */
export const SOCRATIC_SEMINAR_QUESTION_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  textHeading: "The text",
  titleLabel: "Title of the text",
  titlePlaceholder: "e.g. The Great Gatsby",
  authorLabel: "Author (optional)",
  authorPlaceholder: "e.g. F. Scott Fitzgerald",
  themesLabel: "Themes or ideas to discuss — one per line",
  themesPlaceholder: "e.g. the American Dream\nthe past\nclass",
  themesHint: "Up to five works best. Each one shapes several questions.",
  passagesLabel: "Passages to return to — one per line (optional)",
  passagesPlaceholder:
    "e.g. chapter 1, the green light\nchapter 9, the last paragraph",
  passagesHint:
    "A page, paragraph, or line range the questions can send students back to.",
  fallbackTitle: "the text",
  questionsHeading: "Choose your questions",
  questionsIntro:
    "Untick any you do not want, and add your own below each group — honest templates, no AI. Everything you keep goes onto the printable prep sheet.",
  needTheme: "Add at least one theme to get questions built around it.",
  categories: {
    opening: "Opening question",
    core: "Core questions",
    closing: "Closing question",
    world: "World-connection questions",
  },
  categoryHints: {
    opening:
      "Open, and answerable by anyone who has read the text. It gets every voice in the room.",
    core: "Send students back into specific passages and ask them to build an interpretation from the words.",
    closing:
      "Ask what changed or what is still unresolved. It brings the discussion to a close.",
    world: "Carry the ideas outside the text, to students' lives and to today.",
  },
  ownLabel: "Add your own — one per line",
  ownPlaceholder: "Write a question that has no single right answer.",
  issueClosed: "starts like a yes/no question. Try “How …” or “Why …”.",
  issueNoMark: "has no question mark.",
  issueLine: "{question} — {issue}",
  sheetHeading: "Printable options",
  answerLines: "Add ruled lines under each question (for handwritten notes)",
  fileName: "socratic-seminar-prep-sheet",
  document: {
    heading: "Your seminar prep sheet",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining:
      "one: group still without questions|other: groups still without questions",
    complete: "Every group has questions.",
  },
  docTitle: "Socratic Seminar Preparation Sheet",
  subtitle: "{title} by {author}",
  subtitleTitleOnly: "{title}",
  subtitleBlank: "Preparation for a student-led discussion of a text",
  fields: { name: "Name", date: "Date" },
  headings: {
    before: "Before the seminar",
    opening: "Opening question",
    core: "Core questions",
    closing: "Closing question",
    world: "World-connection questions",
    evidence: "Evidence I will cite",
    norms: "Seminar norms",
    starters: "Sentence starters",
    reflection: "After the seminar",
  },
  guidance: {
    opening:
      "Everyone answers this one first. Point to a word or line in the text.",
    core: "Prepare notes on these. Cite the text each time you speak.",
    closing: "Answer this one at the end, once you have heard everyone.",
    world: "Connect the ideas to your own life or to today's world.",
  },
  prompts: {
    opening: "Add an opening question.",
    core: "Add core questions.",
    closing: "Add a closing question.",
    world: "Add a world-connection question.",
  },
  beforeChecklist: [
    "I have read and annotated the text.",
    "I have marked at least three passages I want to discuss.",
    "I have written a question of my own.",
  ],
  evidenceHead: ["Page, paragraph, or line", "What it shows"],
  norms: [
    "Speak to each other, not only to the leader.",
    "Cite the text to support what you say.",
    "Build on or respectfully challenge the ideas of others, not the people.",
    "Let others finish, and make room for quieter voices.",
    "It is fine to change your mind.",
  ],
  starters: [
    "I'd like to build on what ___ said about …",
    "On page ___, the author writes … This suggests …",
    "I see it differently because …",
    "Can you say more about …?",
    "What I hear you saying is … Is that right?",
    "I'm not sure, but I wonder if …",
  ],
  reflectionPrompts: [
    "One idea from the discussion that changed or deepened my thinking:",
    "A question I still have about the text:",
  ],
  frames: SEMINAR_FRAMES,
  example: {
    title: "The Great Gatsby",
    author: "F. Scott Fitzgerald",
    themes: "the American Dream\nthe past\nclass",
    passages: "chapter 1, the green light\nchapter 9, the last paragraph",
  },
} as const;

type Strings = typeof SOCRATIC_SEMINAR_QUESTION_GENERATOR_STRINGS;

const EMPTY_OWN: Record<SeminarCategory, string> = {
  opening: "",
  core: "",
  closing: "",
  world: "",
};

function issueText(issue: QuestionIssue, strings: Strings): string {
  return issue === "closed" ? strings.issueClosed : strings.issueNoMark;
}

function OwnQuestionNotes({
  text,
  strings,
}: {
  text: string;
  strings: Strings;
}) {
  const notes = splitItems(text).flatMap((question) =>
    questionIssues(question).map((issue) => ({
      key: `${question}-${issue}`,
      text: t(strings.issueLine, {
        question,
        issue: issueText(issue, strings),
      }),
    }))
  );
  if (notes.length === 0) {
    return null;
  }
  return (
    <ul aria-live="polite" className="list-disc space-y-1 ps-5">
      {notes.map((note) => (
        <li className={HINT} key={note.key}>
          {note.text}
        </li>
      ))}
    </ul>
  );
}

export default function SocraticSeminarQuestionGeneratorWidget() {
  const strings = useToolUi(SOCRATIC_SEMINAR_QUESTION_GENERATOR_STRINGS);
  const [input, setInput] = useState<SeminarInput>(EMPTY_SEMINAR);
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(new Set());
  const [own, setOwn] = useState(EMPTY_OWN);
  const [answerLines, setAnswerLines] = useState(true);
  const generated = useMemo(
    () => buildSeminarQuestions(input, strings.frames, strings.fallbackTitle),
    [input, strings.frames, strings.fallbackTitle]
  );
  const selected = useMemo(
    () => selectQuestions(generated, excluded, own),
    [generated, excluded, own]
  );
  const doc = useMemo(
    () => buildSeminarSheet(input, selected, { answerLines }, strings),
    [input, selected, answerLines, strings]
  );
  const update = (key: keyof SeminarInput) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const toggle = (question: string) =>
    setExcluded((previous) => {
      const next = new Set(previous);
      if (next.has(question)) {
        next.delete(question);
      } else {
        next.add(question);
      }
      return next;
    });
  const hasTheme = splitItems(input.themes).length > 0;

  return (
    <div className="space-y-8">
      <div className="space-y-6 print:hidden">
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setInput({ ...strings.example });
              setExcluded(new Set());
              setOwn(EMPTY_OWN);
            }}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setInput(EMPTY_SEMINAR);
              setExcluded(new Set());
              setOwn(EMPTY_OWN);
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        <Panel title={strings.textHeading}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="ss-title"
              label={strings.titleLabel}
              onChange={update("title")}
              placeholder={strings.titlePlaceholder}
              value={input.title}
            />
            <TextField
              id="ss-author"
              label={strings.authorLabel}
              onChange={update("author")}
              placeholder={strings.authorPlaceholder}
              value={input.author}
            />
          </div>
          <TextAreaField
            hint={strings.themesHint}
            id="ss-themes"
            label={strings.themesLabel}
            onChange={update("themes")}
            placeholder={strings.themesPlaceholder}
            prose
            rows={3}
            value={input.themes}
          />
          <TextAreaField
            hint={strings.passagesHint}
            id="ss-passages"
            label={strings.passagesLabel}
            onChange={update("passages")}
            placeholder={strings.passagesPlaceholder}
            prose
            rows={2}
            value={input.passages}
          />
        </Panel>
        <section aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.questionsHeading}</h2>
          <p className="text-muted-foreground text-sm">
            {strings.questionsIntro}
          </p>
          {hasTheme ? null : <Notice>{strings.needTheme}</Notice>}
          {SEMINAR_CATEGORIES.map((category) => (
            <Panel key={category} title={strings.categories[category]}>
              <p className={HINT}>{strings.categoryHints[category]}</p>
              <ul className="space-y-2">
                {generated[category].map((question) => (
                  <li key={question}>
                    <Checkbox
                      checked={!excluded.has(question)}
                      label={question}
                      onChange={() => toggle(question)}
                    />
                  </li>
                ))}
              </ul>
              <TextAreaField
                id={`ss-own-${category}`}
                label={strings.ownLabel}
                onChange={(value) =>
                  setOwn((previous) => ({ ...previous, [category]: value }))
                }
                placeholder={strings.ownPlaceholder}
                prose
                rows={2}
                value={own[category]}
              />
              <OwnQuestionNotes strings={strings} text={own[category]} />
            </Panel>
          ))}
        </section>
        <Panel title={strings.sheetHeading}>
          <Checkbox
            checked={answerLines}
            label={strings.answerLines}
            onChange={setAnswerLines}
          />
        </Panel>
      </div>
      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="ss"
        labels={strings.document}
      />
    </div>
  );
}
